/**
 * Canonical user-facing copy for errors handled by application Error Boundaries.
 *
 * @type {Record<string, {
 *   key: string,
 *   description: string,
 *   matcher: { errorNames?: string[], messagePatterns?: string[], fallback?: boolean },
 *   userMessage: { title: string, body: string },
 *   actions: { showReload: boolean, showLogout: boolean }
 * }>}
 */

export const ERROR_BOUNDARY_ERROR_MESSAGES = {
  LAZY_LOAD_TIMEOUT: {
    key: 'LAZY_LOAD_TIMEOUT',
    description: 'A lazy route/component import timed out.',
    matcher: {
      errorNames: ['LazyLoadTimeoutError'],
      messagePatterns: ['timed out'],
    },
    userMessage: {
      title: 'This page is taking longer than expected',
      body: 'Your connection might be slow, or the app may have been updated in the background. Please try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_CHUNK_FETCH_FAILED: {
    key: 'LAZY_CHUNK_FETCH_FAILED',
    description: 'Browser failed to fetch a dynamically imported JS module.',
    matcher: {
      messagePatterns: ['Failed to fetch dynamically imported module'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'This is usually temporary (for example, after a new deployment or due to a network hiccup). Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_MODULE_SCRIPT_IMPORT_FAILED: {
    key: 'LAZY_MODULE_SCRIPT_IMPORT_FAILED',
    description: 'Module script import failed during lazy loading.',
    matcher: {
      messagePatterns: ['Importing a module script failed'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'This is usually temporary (for example, after a new deployment or due to a network hiccup). Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_CSS_PRELOAD_FAILED: {
    key: 'LAZY_CSS_PRELOAD_FAILED',
    description: 'CSS preload failed for a lazily loaded route/page.',
    matcher: {
      messagePatterns: ['Unable to preload CSS'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'This is usually temporary (for example, after a new deployment or due to a network hiccup). Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_CSS_CHUNK_LOAD_FAILED: {
    key: 'LAZY_CSS_CHUNK_LOAD_FAILED',
    description: 'A CSS chunk failed to load.',
    matcher: {
      messagePatterns: ['Loading CSS chunk .* failed'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'This is usually temporary (for example, after a new deployment or due to a network hiccup). Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_CHUNK_LOAD_ERROR: {
    key: 'LAZY_CHUNK_LOAD_ERROR',
    description: 'General chunk loading error raised by runtime.',
    matcher: {
      messagePatterns: ['ChunkLoadError'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'This is usually temporary (for example, after a new deployment or due to a network hiccup). Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  LAZY_REQUEST_ABORTED: {
    key: 'LAZY_REQUEST_ABORTED',
    description:
      'Network request for lazy chunk was aborted (for example navigation interruption).',
    matcher: {
      messagePatterns: ['ERR_ABORTED'],
    },
    userMessage: {
      title: 'We could not load part of the app',
      body: 'Loading was interrupted before the page finished. Please reload and try again.',
    },
    actions: {
      showReload: true,
      showLogout: false,
    },
  },

  RENDER_RUNTIME_ERROR_FALLBACK: {
    key: 'RENDER_RUNTIME_ERROR_FALLBACK',
    description: 'Catch-all for runtime render/lifecycle errors caught by React ErrorBoundary.',
    matcher: {
      fallback: true,
    },
    userMessage: {
      title: 'Oops! Something went wrong',
      body: 'An unexpected error occurred. Please try reloading the page.',
    },
    actions: {
      showReload: true,
      showLogout: true,
    },
  },
}

export default ERROR_BOUNDARY_ERROR_MESSAGES
